import React from "react";
import { useIntl } from "react-intl";
import { msgs } from "../../msgs";
import {
  DeletePromotionInput,
  MultiTogglePromotionsInput,
  MultiUpdatePromotionsInput,
  TogglePromotionInput,
} from "../../types";
import { ApprovalCard, CardHeader, ChangeRow, MetaLabel } from "./shared";
import { PromotionChangeRows } from "./update-summary";

/** Renders the active/inactive state flip for activate/deactivate approvals. */
export function ToggleApprovalSummary({ input, activate }: { input: unknown; activate: boolean }) {
  const intl = useIntl();
  const data = (input ?? {}) as TogglePromotionInput;
  const activeLabel = intl.formatMessage(msgs.approvalStateActive);
  const inactiveLabel = intl.formatMessage(msgs.approvalStateInactive);
  // Target state follows the action; "before" comes from currentlyActive when provided.
  const afterActive = activate;
  const beforeActive = data.currentlyActive ?? !activate;

  return (
    <ApprovalCard>
      {data.ticker && <CardHeader title={data.ticker} />}
      <ChangeRow
        field={intl.formatMessage(msgs.approvalStateChange)}
        before={beforeActive ? activeLabel : inactiveLabel}
        after={afterActive ? activeLabel : inactiveLabel}
      />
    </ApprovalCard>
  );
}

/**
 * Renders the state flip for MULTIPLE DIFFERENT promotions in ONE approval card
 * (activateMultiplePromotions / deactivateMultiplePromotions) — one row per ticker, each with
 * its own before→after, inside a single ApprovalCard instead of one card per promotion.
 */
export function MultiToggleApprovalSummary({
  input,
  activate,
}: {
  input: unknown;
  activate: boolean;
}) {
  const intl = useIntl();
  const data = (input ?? {}) as MultiTogglePromotionsInput;
  const promotions = (Array.isArray(data.promotions) ? data.promotions : []).filter(p => p != null);
  if (promotions.length === 0) return null;

  const activeLabel = intl.formatMessage(msgs.approvalStateActive);
  const inactiveLabel = intl.formatMessage(msgs.approvalStateInactive);

  return (
    <ApprovalCard>
      <CardHeader
        title={intl.formatMessage(msgs.approvalBulkCount, { count: promotions.length })}
      />
      {promotions.map((p, i) => {
        const beforeActive = p.currentlyActive ?? !activate;
        return (
          <ChangeRow
            key={p.ticker ?? i}
            field={p.ticker ?? "—"}
            before={beforeActive ? activeLabel : inactiveLabel}
            after={activate ? activeLabel : inactiveLabel}
          />
        );
      })}
    </ApprovalCard>
  );
}

/**
 * Renders the before→after diff for MULTIPLE DIFFERENT promotions (updateMultiplePromotions) as
 * ONE SEPARATE ApprovalCard PER PROMOTION, stacked inside the same widget — not one big card with
 * a per-ticker row inside it (the earlier layout). Each card uses the exact same header/section
 * visual language as the single-promotion UpdateApprovalSummary card, so reviewing N promotions
 * reads as N familiar cards instead of one dense list; there's no outer "N promociones" header
 * either, since that count is now obvious from the number of cards themselves.
 */
export function MultiUpdateApprovalSummary({ input }: { input: unknown }) {
  const intl = useIntl();
  const data = (input ?? {}) as MultiUpdatePromotionsInput;
  const promotions = (Array.isArray(data.promotions) ? data.promotions : []).filter(p => p != null);
  if (promotions.length === 0) return null;

  return (
    <>
      {promotions.map((p, i) => (
        <ApprovalCard key={p.ticker ?? i}>
          <CardHeader title={p.ticker ?? "—"} />
          <PromotionChangeRows entry={p} intl={intl} />
        </ApprovalCard>
      ))}
    </>
  );
}

/**
 * Renders the deletion impact for delete approvals: the list of engines the promotion
 * will be removed from (aggregator + affected children), taken from the structured
 * `affectedEngines` field. Single-property deletes that don't carry it render nothing here
 * — the generic confirmation message above the card already covers that case.
 */
export function DeleteApprovalSummary({ input }: { input: unknown }) {
  const intl = useIntl();
  const data = (input ?? {}) as DeletePromotionInput;
  const engines = data.affectedEngines ?? [];

  if (engines.length === 0) return null;

  return (
    <ApprovalCard>
      <div className="flex flex-col gap-1.5 pl-3 pr-4 py-3 bg-white">
        <MetaLabel>
          {intl.formatMessage(msgs.approvalDeleteImpact, { count: engines.length })}
        </MetaLabel>
        <div className="flex flex-col gap-1">
          {engines.map(engine => (
            <span
              key={engine}
              className="flex items-center gap-1.5 text-[#212121] font-medium break-all">
              <em className="fa fa-trash-o text-[11px] text-[#EF4444] flex-shrink-0" />
              {engine}
            </span>
          ))}
        </div>
      </div>
    </ApprovalCard>
  );
}
